import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { fetchAvailability } from "@/lib/stockAvailability";
import { formatVariantLabel } from "@/lib/variants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MODES = [
  ["supply", "Tamamını Tedarik Et", "Stok kullanılmaz"],
  ["stock_first", "Stoktan Satış + Kalanı Tedarik Et", "Kullanılabilir kadar stoktan"],
  ["manual", "Stoktan Satış · Manuel Adet", "Stoktan miktarı sen belirle"],
];
const METHODS = [["internal", "İç Üretim"], ["ready_purchase", "Hazır Alım"], ["outsourced", "Fason"]];
const n = (v) => Number(v || 0);
const fmt = (v) => n(v).toLocaleString("tr-TR", { maximumFractionDigits: 2 });

// Sipariş Sihirbazı kalem karşılama kararı: seçilen depodaki fiziksel kullanılabilir stok (yoldaki hariç).
export default function FulfillmentDecision({ draft, patchDraft, variants, supplyType, quoteMode = false }) {
  const [avail, setAvail] = useState([]);
  const [opts, setOpts] = useState([]);
  const pid = draft.product_id;
  const wid = draft.warehouse_id;

  useEffect(() => {
    if (!pid) return;
    fetchAvailability({ productIds: [pid] }).then(setAvail).catch(() => setAvail([]));
    api.get("/inventory/stock-options", { params: { product_id: pid } }).then(({ data }) => setOpts(data || [])).catch(() => setOpts([]));
  }, [pid]);

  const sizeEntries = Object.entries(draft.size_quantities || {}).filter(([, q]) => n(q) > 0);
  const rows = sizeEntries.length
    ? sizeEntries.map(([vid, q]) => ({ vid, qty: n(q) }))
    : [{ vid: draft.variant_id || "", qty: n(draft.quantity) }];
  // Teklifte stok otomatik seçilmez: kullanıcı açıkça Stoktan Satış seçmedikçe tamamı tedarik
  const mode = draft.fulfill_mode || (quoteMode ? "supply" : "stock_first");
  // Varsayılan ürün kartındaki supply_type'tan gelir; yalnız kullanıcı seçimi kalıcıdır.
  const method = (draft.remaining_supply_method_set && draft.remaining_supply_method)
    || (supplyType === "traded" ? "ready_purchase" : "internal");

  const info = (vid) => {
    const a = avail.find((x) => (x.variant_id || "") === vid && x.warehouse_id === wid) || {};
    const option = opts.filter((o) => (o.variant_id || "") === vid && o.warehouse_id === wid && o.kind !== "reserve"
      && o.status === "active" && !o.is_expired).reduce((s, o) => s + n(o.quantity), 0);
    return { physical: n(a.physical), option, reserved: Math.max(0, n(a.reserved_physical) - option), available: n(a.available_physical) };
  };
  const alloc = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      const av = info(r.vid).available;
      out[r.vid] = mode === "supply" ? 0 : mode === "stock_first" ? Math.min(av, r.qty) : n((draft.stock_manual || {})[r.vid]);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows), mode, JSON.stringify(draft.stock_manual || {}), avail, opts, wid]);
  const error = rows.some((r) => alloc[r.vid] < 0 || alloc[r.vid] > r.qty || alloc[r.vid] > info(r.vid).available);
  const totalQty = rows.reduce((s, r) => s + r.qty, 0);
  const ready = !!pid && !!wid && totalQty > 0;
  const prevWid = useRef(wid);
  const prevPid = useRef(pid);

  useEffect(() => {
    if (prevPid.current !== pid && draft.remaining_supply_method_set) patchDraft({ remaining_supply_method_set: false });
    prevPid.current = pid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // Adet 0'a düşerse veya depo değişirse girilmiş stok miktarları sıfırlanır.
  useEffect(() => {
    const whChanged = prevWid.current !== wid;
    prevWid.current = wid;
    if ((whChanged || totalQty <= 0) && Object.keys(draft.stock_manual || {}).length) patchDraft({ stock_manual: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wid, totalQty]);

  useEffect(() => {
    const next = { stock_alloc: alloc, stock_alloc_error: error, fulfill_mode: mode, remaining_supply_method: method };
    if (JSON.stringify(next) !== JSON.stringify({ stock_alloc: draft.stock_alloc, stock_alloc_error: draft.stock_alloc_error,
      fulfill_mode: draft.fulfill_mode, remaining_supply_method: draft.remaining_supply_method })) patchDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(alloc), error, mode, method]);

  if (!ready) return null;
  const remainingTotal = rows.reduce((s, r) => s + Math.max(0, r.qty - alloc[r.vid]), 0);
  const vLabel = (vid) => { const v = variants.find((x) => x.id === vid); return v ? formatVariantLabel(v.attributes, v.sku) : "Genel"; };

  return (
    <div className="mt-3 rounded-sm border border-border p-3 space-y-2" data-testid="fulfill-decision">
      <Label className="text-[10px] uppercase text-muted-foreground font-mono">Karşılama Yöntemi</Label>
      <div className="flex flex-wrap gap-1.5">
        {MODES.map(([v, l, d]) => (
          <button key={v} type="button" data-testid={`fulfill-mode-${v}`} title={d} onClick={() => patchDraft({ fulfill_mode: v, fulfill_mode_user: true })}
            className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${mode === v ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>{l}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="text-muted-foreground">
            <tr><th className="text-left py-1">Varyant</th><th className="text-right">Sipariş</th><th className="text-right">Fiziksel</th>
              <th className="text-right">Rezerve</th><th className="text-right">Opsiyon</th><th className="text-right">Kullanılabilir</th>
              <th className="text-right">Stoktan</th><th className="text-right">Kalan</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const i = info(r.vid);
              const bad = alloc[r.vid] > Math.min(r.qty, i.available) || alloc[r.vid] < 0;
              return (
                <Fragment key={r.vid || "base"}>
                <tr className="border-t border-border/50" data-testid={`fulfill-row-${r.vid || "base"}`}>
                  <td className="py-1">{vLabel(r.vid)}</td><td className="text-right">{fmt(r.qty)}</td>
                  <td className="text-right">{fmt(i.physical)}</td><td className="text-right">{fmt(i.reserved)}</td>
                  <td className="text-right">{fmt(i.option)}</td><td className="text-right text-emerald-500" data-testid={`fulfill-available-${r.vid || "base"}`}>{fmt(i.available)}</td>
                  <td className="text-right">
                    {mode === "manual" ? (
                      <Input data-testid={`fulfill-stock-${r.vid || "base"}`} inputMode="numeric" value={(draft.stock_manual || {})[r.vid] ?? ""}
                        onChange={(e) => patchDraft({ stock_manual: { ...(draft.stock_manual || {}), [r.vid]: e.target.value.replace(/[^\d]/g, "") } })}
                        className={`h-7 w-20 ml-auto text-right text-[11px] ${bad ? "border-rose-500" : ""}`} />
                    ) : fmt(alloc[r.vid])}
                  </td>
                  <td className="text-right">{fmt(Math.max(0, r.qty - alloc[r.vid]))}</td>
                </tr>
                {mode !== "supply" && i.available < r.qty && (
                  <tr data-testid={`fulfill-short-${r.vid || "base"}`}><td colSpan={8} className="pb-1 text-[10px] text-amber-400">
                    ⚠ STOK YETERSİZ – {fmt(r.qty - i.available)} ADET EKSİK (kullanılabilir {fmt(i.available)} / istenen {fmt(r.qty)}) — kalan seçilen tedarik yöntemiyle karşılanır
                  </td></tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && <div className="text-[11px] text-rose-400" data-testid="fulfill-error">Stoktan miktarı sipariş adedini veya kullanılabilir stoğu aşamaz.</div>}
      {remainingTotal > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-muted-foreground">Kalan {fmt(remainingTotal)} adet tedarik yöntemi:</span>
          {METHODS.map(([v, l]) => (
            <button key={v} type="button" data-testid={`fulfill-method-${v}`} onClick={() => patchDraft({ remaining_supply_method: v, remaining_supply_method_set: true })}
              className={`px-2 py-0.5 text-[11px] rounded-sm border ${method === v ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" : "border-border text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground">Yoldaki stok sayılmaz. {quoteMode ? "Teklifte stok DEĞİŞMEZ ve rezerve edilmez; teklif siparişe çevrilince bu depoda rezerve edilir, fiziksel stok sevk kaydında düşer." : "Stoktan miktarı yalnız sipariş oluşturulunca bu depoda rezerve edilir; fiziksel stok sevk kaydında düşer."} Baskı/Nakış uygulamaları tedarikten ayrıdır.</div>
    </div>
  );
}
